import React, { Suspense, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import propaRed from "../../assets/porpa.svg";
import propaRev from "../../assets/propa-reversed.svg";
import { useRegisterMutation } from "../../store/Apis/UserApi";
import { setAuthenticated } from "../../store/slices/UserSlice";
import { RootState } from "../../store/store";
import {
  isStrongPassword,
  isValidEmail,
  isValidPersonName,
} from "../../utils/formValidation";
import "./SignForm.css";
import { TermsModal } from "./TermsModal";
import { trackEvent } from "../../services/appsflyer";
import TagManager from "react-gtm-module";

export function SignForm(props: { next: any }) {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    familyName: "",
    password: "",
    confirm_password: "",
    language: "ar",
  });
  const [errors, setErrors] = useState({});
  const [margin, setMargin] = useState("ml");
  const [register] = useRegisterMutation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const authenticated = useSelector(
    (state: RootState) => state.user.authenticated,
  );
  const [termsAgreed, setTermsAgreed] = useState(false);
  //const user = useSelector((state: RootState) => state.user.user)
  const hasPropaganda = useSelector(
    (state: RootState) => state.user.hasPropaganda,
  );
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (authenticated && hasPropaganda) {
      navigate("/");
    } else if (authenticated) {
      navigate("/");
    }
  }, [authenticated, hasPropaganda, navigate]);

  useEffect(() => {
    const language = i18n.language !== "ar" ? "en" : "ar";
    setMargin(language === "en" ? "mr" : "ml");
    setFormData((prev) =>
      prev.language === language ? prev : { ...prev, language },
    );
  }, [i18n.language]);

  const handleSubmit = (event: any) => {
    event.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length === 0) {
      // Form can be submitted
      register(formData)
        .unwrap()
        .then((data) => {
          TagManager.dataLayer({
            dataLayer: {
              event: "sign_up", user_data: {
                email: formData.email,
                first_name: formData.name,
                family_name: formData.familyName,
                user_id: data?.user?.cuid
              }
            }
          })
          dispatch(setAuthenticated(data));
          trackEvent("af_complete_registration", { platform: 'web', method: 'web' })
          props.next();
        })
        .catch((err) => {
          if (err.data.error_code)
            setErrors({
              error: t("errors." + err.data.error_code) || err.data.error,
            });
          else setErrors({ error: err.data.error });
        });
    } else {
      setErrors(validationErrors);
    }
  };

  const validateInput = (name: string, value: string) => {
    switch (name) {
      case "name":
      case "familyName":
        return isValidPersonName(value)
          ? null
          : t("errors.NAME_LETTERS_ONLY") ||
          "Only letters are allowed (3-200 characters)";
      case "email":
        return isValidEmail(value)
          ? null
          : t("errors.EMAIL_INVALID") || "Please enter a valid email address";
      case "password":
        return isStrongPassword(value)
          ? null
          : t("errors.WEAK_PWD") ||
          "password at least 8 characters with at least 1 number and 1 symbol";
      case "confirm_password":
        return value === formData.password
          ? null
          : t("errors.NOT_MATCHING_PWD") || "passwords not matching";
      default:
        return null;
    }
  };

  const validateForm = () => {
    const validationErrors: any = {};
    for (const [name, value] of Object.entries(formData)) {
      const err = validateInput(name, value);
      if (err) validationErrors[name] = err;
    }
    if (!termsAgreed)
      validationErrors["terms"] = "Please agree to the terms and conditions.";
    return validationErrors;
  };

  const handleChange = (event: any) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    //setErrors({ ...errors, [event.target.name]: validateInput(event.target.name, event.target.value) });
  };
  const handleTermsChange = (event: any) => {
    setTermsAgreed(event.target.checked);
  };

  return (
    <Suspense>
      {showModal && (
        <TermsModal
          params={""}
          setShowModal={() => setShowModal(false)}
        ></TermsModal>
      )}

      <div className="w-full h-full flex flex-row">
        <div className="w-[20%] h-full flex items-end justify-end">
          <img alt="retro" className="h-[55%] mb-[20%]" src={propaRed}></img>
        </div>
        <form
          onSubmit={handleSubmit}
          className="w-[60%] h-full flex flex-col bg-orange items-end justify-center phone:hidden web:visible"
        >
          <div className="w-full flex flex-col items-center justify-center gap-4">
            <h2 className="font-Inter font-bold text-3xl">
              {t("signin.register_heading")}
            </h2>
            <div className="w-[80%] flex flex-row gap-4">
              <input
                className="w-[50%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg placeholder:text-rose-500 px-4"
                placeholder={t("signin.name") || "name"}
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                min="3"
                required
              ></input>
              <input
                className="w-[50%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg placeholder:text-rose-500 px-4"
                placeholder={t("signin.family") || "family name"}
                type="text"
                name="familyName"
                value={formData.familyName}
                onChange={handleChange}
                min="3"
                required
              ></input>
            </div>
            <div className="w-[80%] flex flex-row gap-4">
              <input
                className="w-[100%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg placeholder:text-rose-500 px-4"
                placeholder={t("signin.email") || "email"}
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              ></input>
            </div>
            <div className="w-[80%] flex flex-row gap-4">
              <input
                className="w-[50%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg placeholder:text-rose-500 px-4"
                placeholder={t("signin.pwd") || "password"}
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
              ></input>
              <input
                className="w-[50%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg placeholder:text-rose-500 px-4"
                placeholder={t("signin.confirm_pwd") || "confirm password"}
                type="password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                required
              ></input>
            </div>

            <div className="w-[80%] flex flex-row items-center px-2 py-2">
              <input
                type="checkbox"
                name="terms"
                checked={termsAgreed}
                onChange={handleTermsChange}
              ></input>
              <h3 className=" ps-3 text-lg text-black font-Inter font-bold mt-1 ">
                {t("signin.terms1")}
              </h3>
              <h3
                className="ps-1 text-lg text-black font-Inter font-bold mt-1 underline cursor-pointer"
                onClick={() => setShowModal(true)}
              >
                {t("signin.terms2")}
              </h3>
            </div>
          </div>
          {Object.entries(errors).length > 0 && (
            <ul className="w-full flex flex-col items-center justify-center">
              {Object.entries(errors).map(([key, value], index) => {
                if (value)
                  return (
                    <li
                      key={index}
                      className="web:text-red phone:text-yellow-400 text-sm font-bold w-[80%] "
                    >
                      {value.toString()}
                    </li>
                  );
                else return null;
              })}
            </ul>
          )}
          <button
            className={`w-[30%] ${margin}-[10%] h-12 bg-light-blue p-2 mt-4 border-2 font-Inter font-bold border-black shadow-lg`}
            type="submit"
          >
            {t("signin.subscribe")}
          </button>
          <p className="mt-3 w-full text-center text-lg font-Inter text-black phone:hidden web:block">
            {t("signin.have_account")}{" "}
            <Link
              to="/login"
              className="font-bold underline decoration-2 underline-offset-2 hover:opacity-90 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red focus-visible:ring-offset-2 focus-visible:ring-offset-orange"
            >
              {t("signin.log_in")}
            </Link>
          </p>
        </form>
        <form
          onSubmit={handleSubmit}
          className="flex overflow-y-auto pb-8 flex-col justify-start gap-y-4 px-4 w-full h-full bg-red items-center web:hidden phone:visible"
        >
          <h2 className="font-Inter font-bold text-xl">
            {t("signin.register_heading")}
          </h2>
          <div className="w-full flex flex-col items-center justify-center">
            <input
              className="w-[100%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg shadow-black placeholder:text-rose-500 px-6 "
              placeholder={t("signin.name") || "name"}
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
            ></input>
            <input
              className="w-[100%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg shadow-black placeholder:text-rose-500 px-6 mt-4"
              placeholder={t("signin.family") || "family name"}
              type="text"
              name="familyName"
              value={formData.familyName}
              onChange={handleChange}
            ></input>
            <input
              className="w-[100%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg shadow-black placeholder:text-rose-500 px-6 mt-4"
              placeholder={t("signin.email") || "email"}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
            ></input>
            <input
              className="w-[100%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg shadow-black placeholder:text-rose-500 px-6 mt-4"
              placeholder={t("signin.pwd") || "password"}
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
            ></input>
            <input
              className="w-[100%] h-12 bg-landing-yellow border-2 border-red font-Inter shadow-lg shadow-black placeholder:text-rose-500 px-6 mt-4"
              placeholder={t("signin.confirm_pwd") || "confirm password"}
              type="password"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleChange}
            ></input>
            <div className="w-[100%] flex flex-row items-center px-2 pt-4">
              <input
                type="checkbox"
                checked={termsAgreed}
                onChange={handleTermsChange}
              ></input>
              <h3 className="ps-2 font-bold text-lg text-black font-Inter">
                {t("signin.terms1")}
              </h3>
              <h3
                className="ps-1 font-bold text-lg text-black font-Inter underline cursor-pointer"
                onClick={() => setShowModal(true)}
              >
                {t("signin.terms2")}
              </h3>
            </div>
          </div>
          {Object.entries(errors).length > 0 && (
            <ul className="w-full flex flex-col items-start justify-center">
              {Object.entries(errors).map(([key, value], index) => {
                if (value)
                  return (
                    <li
                      key={index}
                      className="web:text-red phone:text-yellow-400 text-sm w-[80%] "
                    >
                      {value.toString()}
                    </li>
                  );
                else return null;
              })}
            </ul>
          )}
          <input
            className="w-[100%] h-12 bg-light-blue p-2 border-2 font-Inter font-bold border-black shadow-lg z-50"
            type="submit"
            value={t("signin.subscribe") || "Sign in"}
          />
          <p className="w-full text-center text-sm text-landing-yellow font-Inter web:hidden phone:block">
            {t("signin.have_account")}{" "}
            <Link
              to="/login"
              className="font-bold underline decoration-2 underline-offset-2 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-landing-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-red"
            >
              {t("signin.log_in")}
            </Link>
          </p>
        </form>
        <div className="w-[20%] h-full flex items-end justify-start">
          <img
            alt="reversed retro"
            className="h-[55%] mb-[20%]"
            src={propaRev}
          ></img>
        </div>
      </div>
    </Suspense>
  );
}
